import { FC, useMemo } from 'react';
import ReactImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import { useIntl } from 'react-intl';

import { alpha } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { calculateRatioDimensions, OFFERING_VIDEOS } from '@arrived/common';
import { Offering, Photo } from '@arrived/models';

import { BackButton, ForwardButton } from '../../ArrowNavButton';
import { PropertyThumbnailProps, PropertyVideo } from '../../Property';

import { useIsMobile, useMediaQueryClasses } from '../../hooks';

interface PropertyDetailsCarouselProps {
  offering: Offering;
  photos: Photo[];
  propertyName: string;
  PropertyThumbnailComponent: FC<PropertyThumbnailProps>;
}

const StyledPropertyDetailsCarousel = styled.div`
  &.property-details-carousel-wrapper {
    display: flex;
    position: relative;

    .property-details-status-flag {
      position: absolute;
      display: none;
      top: 0;
      left: 0;
    }

    .property-details-carousel {
      display: flex;
      flex: 1 1 auto;

      &.fullscreen-modal {
        height: 100%;
      }

      .image-gallery-custom-left-nav,
      .image-gallery-custom-right-nav {
        position: absolute;
        top: 50%;
        z-index: 3;
        transform: translateY(-50%);
        padding: ${({ theme }) => theme.spacing(1)};
        background-color: ${({ theme }) => alpha(theme.palette.neutrals.platinum, 0.85)};

        &:hover {
          background-color: ${({ theme }) => theme.palette.neutrals.milk};
        }
      }

      .image-gallery-custom-left-nav {
        left: ${({ theme }) => theme.spacing(4)};
      }

      .image-gallery-custom-right-nav {
        right: ${({ theme }) => theme.spacing(4)};
      }

      .image-gallery-content {
        display: flex;
        flex: 1 1 auto;

        &.fullscreen {
          .image-gallery-slide-wrapper {
            position: relative;
            display: flex;
            width: auto;

            .image-gallery-swipe {
              display: flex;
              flex: 1 1 auto;

              .image-gallery-slides {
                display: flex;
                flex: 1 1 auto;

                .image-gallery-slide {
                  display: flex;
                  flex: 1 1 auto;
                  right: 0;
                  bottom: 0;

                  .property-thumbnail-wrapper {
                    display: flex;
                    flex: 1 1 auto;

                    .property-thumbnail-image-wrapper {
                      display: flex;
                      flex: 1 1 auto;

                      .property-thumbnail-image {
                        object-fit: contain;
                      }
                    }
                  }
                }
              }
            }
          }

          .image-gallery-thumbnails-wrapper {
            display: none;
          }
        }
      }

      .image-gallery-slide-wrapper.right {
        flex: 1 1 auto;
        width: 80%;

        .image-gallery-icon {
          visibility: hidden;
          opacity: 0;
          transition: visibility 0s linear 250ms, opacity 250ms;
        }

        &:hover {
          .image-gallery-icon {
            visibility: visible;
            opacity: 1;
            transition: visibility 0s linear 250ms, opacity 250ms;
          }
        }
      }

      .image-gallery-thumbnails-wrapper.right {
        flex: 0 0 auto;
        width: 20%;
        margin: 0;
        height: auto !important; // Important required because of an inline height being applied from the 3p lib

        .property-details-carousel-thumbnail {
          width: 100%;
          margin-top: 0;

          &:first-child {
            display: none;
          }

          &.active,
          &:hover,
          &:focus {
            border-color: ${({ theme }) => theme.palette.tertiary[500]};
          }
        }
      }

      .property-thumbnail-wrapper {
        width: 100%;
      }

      .image-gallery-icon:hover {
        color: ${({ theme }) => theme.palette.tertiary[500]};
      }
    }

    &.mobile {
      .property-details-status-flag {
        display: unset;
      }

      .property-details-carousel {
        .image-gallery-slide-wrapper.right {
          width: auto;

          .image-gallery-icon {
            &,
            &:hover {
              visibility: visible;
              opacity: 1;
            }
          }
        }
      }
    }
  }
`;

export const PropertyDetailsCarousel = ({
  offering,
  photos,
  propertyName,
  PropertyThumbnailComponent,
}: PropertyDetailsCarouselProps) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const mediaQueryClasses = useMediaQueryClasses();

  const mainImageDimensions = useMemo(
    () =>
      calculateRatioDimensions({
        width: isMobile ? '360px' : '720px',
        aspectRatio: '373:224',
      }),
    [isMobile]
  );

  const thumbnailDimensions = useMemo(() => calculateRatioDimensions({ width: '170px', aspectRatio: '85:73' }), []);

  const items = useMemo((): ReactImageGalleryItem[] => {
    const items: ReactImageGalleryItem[] = photos
      ? photos.map((photo) => {
          const { url } = photo;

          return {
            original: url,
            originalAlt: propertyName,
            originalClass: 'property-details-carousel-main',
            thumbnail: url,
            thumbnailAlt: propertyName,
            thumbnailClass: 'property-details-carousel-thumbnail',
          };
        })
      : [];

    const offeringVideo = OFFERING_VIDEOS[offering.id];

    if (offeringVideo) {
      const youtubeId = typeof offeringVideo === 'string' ? offeringVideo : offeringVideo.youtubeId;
      const position =
        typeof offeringVideo === 'string' ? 0 : Math.max(0, Math.min(offeringVideo.position, items.length));
      const image = `https://img.youtube.com/vi/${youtubeId}/0.jpg`;

      items.splice(position, 0, {
        original: image,
        originalAlt: propertyName,
        originalClass: 'property-details-carousel-main',
        renderItem: () => (
          <PropertyVideo
            offering={offering}
            video={youtubeId}
            isScalableImage
            propertyName={propertyName}
            thumbnailHeight={mainImageDimensions.height}
            thumbnailRatio={mainImageDimensions.aspectRatio}
            thumbnailWidth={mainImageDimensions.width}
          />
        ),
        thumbnail: image,
        thumbnailAlt: propertyName,
        thumbnailClass: 'property-details-carousel-thumbnail',
      });
    }

    return items;
  }, [mainImageDimensions, offering, photos, propertyName]);

  return (
    <StyledPropertyDetailsCarousel aria-hidden className={clsx('property-details-carousel-wrapper', mediaQueryClasses)}>
      <ReactImageGallery
        additionalClass="property-details-carousel"
        infinite={true}
        items={items}
        renderItem={(args: ReactImageGalleryItem) =>
          args.renderItem ? (
            args.renderItem(args)
          ) : (
            <PropertyThumbnailComponent
              dataE2e="property-details-carousel-hero"
              isScalableImage
              offering={offering}
              propertyName={propertyName}
              thumbnailHeight={mainImageDimensions.height}
              thumbnailRatio={mainImageDimensions.aspectRatio}
              thumbnailSrc={args.original}
              thumbnailWidth={mainImageDimensions.width}
            />
          )
        }
        renderThumbInner={(args: ReactImageGalleryItem) =>
          args.renderThumbInner ? (
            args.renderThumbInner(args)
          ) : (
            <PropertyThumbnailComponent
              dataE2e="property-details-carousel-thumbnail"
              isScalableImage
              propertyName={propertyName}
              thumbnailHeight={thumbnailDimensions.height}
              thumbnailRatio={thumbnailDimensions.aspectRatio}
              thumbnailSrc={args.original}
              thumbnailWidth={thumbnailDimensions.width}
            />
          )
        }
        renderLeftNav={(onClick, disabled) => {
          return (
            <BackButton
              edge="start"
              title={intl.formatMessage({ id: 'previous', defaultMessage: 'Previous' })}
              disabled={disabled}
              onClick={onClick}
              className="image-gallery-icon image-gallery-custom-left-nav"
            />
          );
        }}
        renderRightNav={(onClick, disabled) => {
          return (
            <ForwardButton
              edge="end"
              title={intl.formatMessage({ id: 'next', defaultMessage: 'Next' })}
              disabled={disabled}
              onClick={onClick}
              className="image-gallery-icon image-gallery-custom-right-nav"
            />
          );
        }}
        showFullscreenButton={false}
        showPlayButton={false}
        showThumbnails={!isMobile}
        thumbnailPosition="right"
        useBrowserFullscreen={false}
      />
    </StyledPropertyDetailsCarousel>
  );
};
